/**
 * Background sync queue — stores offline mutations and replays them when online.
 * Integrates with useNetworkStatus to auto-trigger sync on reconnect.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNetworkStatus } from './useNetworkStatus.js';
import {
  type SyncQueueItem,
  getSyncQueue,
  removeFromSyncQueue,
  updateSyncQueueItem,
  addToSyncQueue,
  getSyncQueueCount,
  getOfflinePhoto,
  removeOfflinePhoto,
} from './useIndexedDB.js';

const MAX_RETRIES = 3;

interface SyncState {
  pendingCount: number;
  isSyncing: boolean;
  lastSyncAt: number | null;
  lastError: string | null;
}

export function useSyncQueue() {
  const { isOnline } = useNetworkStatus();
  const queryClient = useQueryClient();
  const [state, setState] = useState<SyncState>({
    pendingCount: 0,
    isSyncing: false,
    lastSyncAt: null,
    lastError: null,
  });
  const syncingRef = useRef(false);

  // Load pending count on mount
  useEffect(() => {
    getSyncQueueCount().then(count => setState(s => ({ ...s, pendingCount: count })));
  }, []);

  const processQueue = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setState(s => ({ ...s, isSyncing: true, lastError: null }));

    try {
      const queue = await getSyncQueue();
      let processed = 0;

      for (const item of queue) {
        try {
          await processItem(item);
          await removeFromSyncQueue(item.id);

          // Clean up offline photo if applicable
          if (item.type === 'foto_upload' && item.fileData) {
            await removeOfflinePhoto(item.id).catch(() => {});
          }

          processed++;
          setState(s => ({ ...s, pendingCount: Math.max(0, s.pendingCount - 1) }));
        } catch (err) {
          const newRetries = item.retries + 1;
          if (newRetries >= MAX_RETRIES) {
            // Give up after max retries
            await removeFromSyncQueue(item.id);
            setState(s => ({
              ...s,
              pendingCount: Math.max(0, s.pendingCount - 1),
              lastError: `Sync mislukt voor ${item.type}: ${(err as Error).message}`,
            }));
          } else {
            await updateSyncQueueItem({ ...item, retries: newRetries });
          }
        }
      }

      if (processed > 0) {
        // Invalidate relevant queries to refresh data
        await queryClient.invalidateQueries({ queryKey: ['buitendienst'] });
      }

      setState(s => ({ ...s, isSyncing: false, lastSyncAt: Date.now() }));
    } catch (err) {
      setState(s => ({
        ...s,
        isSyncing: false,
        lastError: `Sync fout: ${(err as Error).message}`,
      }));
    } finally {
      syncingRef.current = false;
    }
  }, [queryClient]);

  // Auto-sync when coming back online
  useEffect(() => {
    if (isOnline && state.pendingCount > 0) {
      processQueue();
    }
  }, [isOnline, state.pendingCount, processQueue]);

  // Periodic sync attempt every 30s when online and items pending
  useEffect(() => {
    if (!isOnline || state.pendingCount === 0) return;
    const interval = setInterval(processQueue, 30_000);
    return () => clearInterval(interval);
  }, [isOnline, state.pendingCount, processQueue]);

  const enqueue = useCallback(async (item: Omit<SyncQueueItem, 'createdAt' | 'retries'>) => {
    const fullItem: SyncQueueItem = {
      ...item,
      createdAt: Date.now(),
      retries: 0,
    };
    await addToSyncQueue(fullItem);
    setState(s => ({ ...s, pendingCount: s.pendingCount + 1 }));

    // Try immediate sync if online
    if (navigator.onLine) {
      setTimeout(processQueue, 100);
    }
  }, [processQueue]);

  return {
    ...state,
    enqueue,
    processQueue,
  };
}

// ─── Process individual queue items ────────────────────────────

async function processItem(item: SyncQueueItem): Promise<void> {
  const token = localStorage.getItem('accessToken');
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  if (item.type === 'foto_upload' && item.fileData) {
    // Reconstruct FormData for file upload
    const formData = new FormData();
    const blob = new Blob([item.fileData.buffer], { type: item.fileData.type });
    const file = new File([blob], item.fileData.name, { type: item.fileData.type });
    formData.append('bestand', file);

    if (item.metadata) {
      for (const [key, value] of Object.entries(item.metadata)) {
        formData.append(key, value);
      }
    }

    const res = await fetch(item.url, {
      method: item.method,
      headers,
      body: formData,
    });

    if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  } else {
    // JSON request
    headers['Content-Type'] = 'application/json';
    const res = await fetch(item.url, {
      method: item.method,
      headers,
      body: item.body ? JSON.stringify(item.body) : undefined,
    });

    if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  }
}
